"use client"

import { useState } from "react"
import { useAuth } from "@/hooks/useAuth"
import {
  listJobs,
  listWorkflowJobs,
  getJobLogs,
} from "@/services/hpcJobsService"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Bug, Server } from "lucide-react"
import LoadingSpinner from "@/components/shared/LoadingSpinner"

const PAGE_TITLE = "HPC Jobs (Debug)"

/**
 * State for a single endpoint test section.
 * Tracks loading state, the raw JSON result, and any error message.
 */
interface EndpointState {
  isLoading: boolean
  result: unknown | null
  error: string | null
}

/** Initial state for an endpoint section. */
const INITIAL_STATE: EndpointState = { isLoading: false, result: null, error: null }

/**
 * Renders a raw JSON response in a scrollable pre block.
 *
 * @param data - The data to display as formatted JSON.
 */
function JsonResult({ data }: { data: unknown }) {
  return (
    <pre className="max-h-96 overflow-auto rounded-md bg-muted p-4 text-xs font-mono whitespace-pre-wrap">
      {JSON.stringify(data, null, 2)}
    </pre>
  )
}

/**
 * Debug interface for testing the HPC Jobs proxy Lambda endpoints.
 *
 * This component is a developer tool — NOT the production HPC jobs UI.
 * It allows manually invoking each endpoint with custom parameters and
 * inspecting the raw API response. Only accessible in dev mode.
 */
export default function HpcJobsDebugInterface() {
  const { getAccessToken } = useAuth()

  // --- List Jobs state ---
  const [listJobsState, setListJobsState] = useState<EndpointState>(INITIAL_STATE)
  const [listJobsUser, setListJobsUser] = useState<string>("")
  const [listJobsFinished, setListJobsFinished] = useState<boolean>(false)
  const [listJobsLimit, setListJobsLimit] = useState<string>("20")

  // --- Workflow Jobs state ---
  const [workflowState, setWorkflowState] = useState<EndpointState>(INITIAL_STATE)
  const [workflowId, setWorkflowId] = useState<string>("")

  // --- Job Logs state ---
  const [logsState, setLogsState] = useState<EndpointState>(INITIAL_STATE)
  const [logsJobId, setLogsJobId] = useState<string>("")
  const [logsHead, setLogsHead] = useState<boolean>(false)
  const [logsLimit, setLogsLimit] = useState<string>("50")
  const [logsStartTime, setLogsStartTime] = useState<string>("")
  const [logsEndTime, setLogsEndTime] = useState<string>("")

  /**
   * Handles the "Test" button for the List Jobs endpoint.
   * Calls the list jobs API with the current form parameters.
   */
  async function handleListJobs(): Promise<void> {
    setListJobsState({ isLoading: true, result: null, error: null })
    try {
      const token = await getAccessToken()
      const result = await listJobs(
        {
          user: listJobsUser || undefined,
          finished: listJobsFinished,
          limit: listJobsLimit ? parseInt(listJobsLimit, 10) : undefined,
        },
        token
      )
      setListJobsState({ isLoading: false, result, error: null })
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error"
      setListJobsState({ isLoading: false, result: null, error: message })
    }
  }

  /**
   * Handles the "Test" button for the Workflow Jobs endpoint.
   * Calls the workflow jobs API with the specified workflow ID.
   */
  async function handleWorkflowJobs(): Promise<void> {
    if (!workflowId.trim()) return
    setWorkflowState({ isLoading: true, result: null, error: null })
    try {
      const token = await getAccessToken()
      const result = await listWorkflowJobs({user: listJobsUser || "", workflowId: workflowId.trim() }, token)
      setWorkflowState({ isLoading: false, result, error: null })
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error"
      setWorkflowState({ isLoading: false, result: null, error: message })
    }
  }

  /**
   * Handles the "Test" button for the Job Logs endpoint.
   * Calls the logs API with the specified parameters.
   */
  async function handleJobLogs(): Promise<void> {
    if (!logsJobId.trim()) return
    setLogsState({ isLoading: true, result: null, error: null })
    try {
      const token = await getAccessToken()
      const result = await getJobLogs(
        {
          jobId: logsJobId.trim(),
          head: logsHead,
          limit: logsLimit ? parseInt(logsLimit, 10) : undefined,
          startTime: logsStartTime || undefined,
          endTime: logsEndTime || undefined,
        },
        token
      )
      setLogsState({ isLoading: false, result, error: null })
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error"
      setLogsState({ isLoading: false, result: null, error: message })
    }
  }

  return (
    <>
    <title>{PAGE_TITLE} - MAP Apps Platform</title>
    <div className="flex flex-1 flex-col min-h-0 overflow-y-auto p-6">
      <div className="flex flex-col gap-6 max-w-4xl">
        {/* Page header */}
        <div className="flex items-center gap-3">
          <Bug className="h-6 w-6 text-orange-500" />
          <div>
            <h1>HPC Jobs Lambda — Debug</h1>
            <p className="text-muted-foreground mt-1">
              Developer tool for testing the HPC jobs proxy Lambda endpoints with your current auth
              context.
            </p>
          </div>
        </div>

        {/* ─── List Jobs ─────────────────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">List Jobs</CardTitle>
            <CardDescription>
              <code>GET /api/hpc-jobs/jobs</code> — List Batch jobs with optional filters.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex flex-wrap gap-4 items-end">
                <div className="space-y-1">
                  <label htmlFor="list-jobs-user" className="text-sm font-medium">
                    User
                  </label>
                  <Input
                    id="list-jobs-user"
                    value={listJobsUser}
                    onChange={e => setListJobsUser(e.target.value)}
                    placeholder="all"
                    className="w-40"
                  />
                </div>
                <div className="space-y-1">
                  <label htmlFor="list-jobs-limit" className="text-sm font-medium">
                    Limit
                  </label>
                  <Input
                    id="list-jobs-limit"
                    type="number"
                    value={listJobsLimit}
                    onChange={e => setListJobsLimit(e.target.value)}
                    className="w-24"
                  />
                </div>
                <div className="flex items-center gap-2 pb-1">
                  <input
                    id="list-jobs-finished"
                    type="checkbox"
                    checked={listJobsFinished}
                    onChange={e => setListJobsFinished(e.target.checked)}
                    className="h-4 w-4"
                  />
                  <label htmlFor="list-jobs-finished" className="text-sm font-medium">
                    Include finished
                  </label>
                </div>
              </div>
              <Button onClick={handleListJobs} disabled={listJobsState.isLoading}>
                {listJobsState.isLoading ? (
                  <LoadingSpinner loadingContent="Calling..." />
                ) : (
                  <>
                    <Server className="mr-2 h-4 w-4" />
                    Test list_jobs
                  </>
                )}
              </Button>
              {listJobsState.error && (
                <p className="text-sm text-destructive">{listJobsState.error}</p>
              )}
              {listJobsState.result !== null && <JsonResult data={listJobsState.result} />}
            </div>
          </CardContent>
        </Card>

        {/* ─── Workflow Jobs ─────────────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Workflow Jobs</CardTitle>
            <CardDescription>
              <code>GET /api/hpc-jobs/workflow-jobs</code> — List jobs within a workflow.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-1">
                <label htmlFor="workflow-id" className="text-sm font-medium">
                  Workflow ID *
                </label>
                <Input
                  id="workflow-id"
                  value={workflowId}
                  onChange={e => setWorkflowId(e.target.value)}
                  placeholder="e.g. abc123-def456"
                  className="w-80"
                />
              </div>
              <Button
                onClick={handleWorkflowJobs}
                disabled={workflowState.isLoading || !workflowId.trim()}
              >
                {workflowState.isLoading ? (
                  <LoadingSpinner loadingContent="Calling..." />
                ) : (
                  <>
                    <Server className="mr-2 h-4 w-4" />
                    Test list_workflow_jobs
                  </>
                )}
              </Button>
              {workflowState.error && (
                <p className="text-sm text-destructive">{workflowState.error}</p>
              )}
              {workflowState.result !== null && <JsonResult data={workflowState.result} />}
            </div>
          </CardContent>
        </Card>

        {/* ─── Job Logs ──────────────────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Job Logs</CardTitle>
            <CardDescription>
              <code>GET /api/hpc-jobs/logs</code> — Fetch log events for a Batch job.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex flex-wrap gap-4 items-end">
                <div className="space-y-1">
                  <label htmlFor="logs-job-id" className="text-sm font-medium">
                    Job ID *
                  </label>
                  <Input
                    id="logs-job-id"
                    value={logsJobId}
                    onChange={e => setLogsJobId(e.target.value)}
                    placeholder="e.g. 12345678-abcd-efgh"
                    className="w-80"
                  />
                </div>
                <div className="space-y-1">
                  <label htmlFor="logs-limit" className="text-sm font-medium">
                    Limit
                  </label>
                  <Input
                    id="logs-limit"
                    type="number"
                    value={logsLimit}
                    onChange={e => setLogsLimit(e.target.value)}
                    className="w-24"
                  />
                </div>
                <div className="flex items-center gap-2 pb-1">
                  <input
                    id="logs-head"
                    type="checkbox"
                    checked={logsHead}
                    onChange={e => setLogsHead(e.target.checked)}
                    className="h-4 w-4"
                  />
                  <label htmlFor="logs-head" className="text-sm font-medium">
                    Head (oldest first)
                  </label>
                </div>
              </div>
              <div className="flex flex-wrap gap-4">
                <div className="space-y-1">
                  <label htmlFor="logs-start-time" className="text-sm font-medium">
                    Start Time
                  </label>
                  <Input
                    id="logs-start-time"
                    value={logsStartTime}
                    onChange={e => setLogsStartTime(e.target.value)}
                    placeholder="epoch ms or ISO"
                    className="w-52"
                  />
                </div>
                <div className="space-y-1">
                  <label htmlFor="logs-end-time" className="text-sm font-medium">
                    End Time
                  </label>
                  <Input
                    id="logs-end-time"
                    value={logsEndTime}
                    onChange={e => setLogsEndTime(e.target.value)}
                    placeholder="epoch ms or ISO"
                    className="w-52"
                  />
                </div>
              </div>
              <Button onClick={handleJobLogs} disabled={logsState.isLoading || !logsJobId.trim()}>
                {logsState.isLoading ? (
                  <LoadingSpinner loadingContent="Calling..." />
                ) : (
                  <>
                    <Server className="mr-2 h-4 w-4" />
                    Test get_logs
                  </>
                )}
              </Button>
              {logsState.error && <p className="text-sm text-destructive">{logsState.error}</p>}
              {logsState.result !== null && <JsonResult data={logsState.result} />}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
    </>
  )
}
